/**
 * Permission Management Hook
 *
 * Provides business logic and API integration for managing roles and rights
 */

import { useState, useCallback } from 'react';
import { useRoleService, useRightService } from '@actschurch/ccms-sdk';
import { toast } from '../../../../components/ui/providers/AlertProvider';
import type {
  RoleModelView,
  RightModelView,
  CreateRoleRequest,
  UpdateRoleRequest,
  CreateRightRequest,
  UpdateRightRequest,
} from '../types';

export function usePermissionManagement() {
  const roleService = useRoleService();
  const rightService = useRightService();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ========================================
  // Role Operations
  // ========================================

  const fetchRoles = useCallback(async (): Promise<RoleModelView[]> => {
    setLoading(true);
    setError(null);
    try {
      const data = await roleService.getAll();
      return data;
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to fetch roles';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [roleService]);

  const createRole = useCallback(
    async (data: CreateRoleRequest): Promise<RoleModelView> => {
      setLoading(true);
      setError(null);
      try {
        const result = await toast.promise(roleService.create(data), {
          loading: 'Creating role...',
          success: 'Role created successfully',
          error: (err) => `Failed to create role: ${err.message}`,
        });
        return result;
      } catch (err: any) {
        const errorMessage = err?.message || 'Failed to create role';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [roleService]
  );

  const updateRole = useCallback(
    async (id: string, data: UpdateRoleRequest): Promise<RoleModelView> => {
      setLoading(true);
      setError(null);
      try {
        const result = await toast.promise(roleService.update(id, data), {
          loading: 'Updating role...',
          success: 'Role updated successfully',
          error: (err) => `Failed to update role: ${err.message}`,
        });
        return result;
      } catch (err: any) {
        const errorMessage = err?.message || 'Failed to update role';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [roleService]
  );

  const deleteRole = useCallback(
    async (id: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        await toast.promise(roleService.delete(id), {
          loading: 'Deleting role...',
          success: 'Role deleted successfully',
          error: (err) => `Failed to delete role: ${err.message}`,
        });
      } catch (err: any) {
        const errorMessage = err?.message || 'Failed to delete role';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [roleService]
  );

  const getRoleById = useCallback(
    async (id: string): Promise<RoleModelView> => {
      setLoading(true);
      setError(null);
      try {
        const result = await roleService.getById(id);
        return result;
      } catch (err: any) {
        const errorMessage = err?.message || 'Failed to fetch role';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [roleService]
  );

  // ========================================
  // Right Operations
  // ========================================

  const fetchRights = useCallback(async (): Promise<RightModelView[]> => {
    setLoading(true);
    setError(null);
    try {
      const data = await rightService.getAll();
      return data;
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to fetch rights';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [rightService]);

  const createRight = useCallback(
    async (data: CreateRightRequest): Promise<RightModelView> => {
      setLoading(true);
      setError(null);
      try {
        const result = await toast.promise(rightService.create(data), {
          loading: 'Creating right...',
          success: 'Right created successfully',
          error: (err) => `Failed to create right: ${err.message}`,
        });
        return result;
      } catch (err: any) {
        const errorMessage = err?.message || 'Failed to create right';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [rightService]
  );

  const updateRight = useCallback(
    async (id: number, data: UpdateRightRequest): Promise<RightModelView> => {
      setLoading(true);
      setError(null);
      try {
        const result = await toast.promise(rightService.update(id, data), {
          loading: 'Updating right...',
          success: 'Right updated successfully',
          error: (err) => `Failed to update right: ${err.message}`,
        });
        return result;
      } catch (err: any) {
        const errorMessage = err?.message || 'Failed to update right';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [rightService]
  );

  const deleteRight = useCallback(
    async (id: number): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        await toast.promise(rightService.delete(id), {
          loading: 'Deleting right...',
          success: 'Right deleted successfully',
          error: (err) => `Failed to delete right: ${err.message}`,
        });
      } catch (err: any) {
        const errorMessage = err?.message || 'Failed to delete right';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [rightService]
  );

  // ========================================
  // Rights Assignment Operations
  // ========================================

  const fetchRoleRights = useCallback(
    async (roleId: string): Promise<RightModelView[]> => {
      setLoading(true);
      setError(null);
      try {
        const data = await rightService.getByRole(roleId);
        return data;
      } catch (err: any) {
        const errorMessage = err?.message || 'Failed to fetch role rights';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [rightService]
  );

  const assignRightToRole = useCallback(
    async (roleId: string, rightId: number): Promise<boolean> => {
      try {
        const result = await rightService.assignToRole(roleId, rightId);
        return result;
      } catch (err: any) {
        throw err;
      }
    },
    [rightService]
  );

  const revokeRightFromRole = useCallback(
    async (roleId: string, rightId: number): Promise<boolean> => {
      try {
        const result = await rightService.revokeFromRole(roleId, rightId);
        return result;
      } catch (err: any) {
        throw err;
      }
    },
    [rightService]
  );

  return {
    loading,
    error,
    // Role operations
    fetchRoles,
    createRole,
    updateRole,
    deleteRole,
    getRoleById,
    // Right operations
    fetchRights,
    createRight,
    updateRight,
    deleteRight,
    // Rights assignment
    fetchRoleRights,
    assignRightToRole,
    revokeRightFromRole,
  };
}
